import { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Box, Flex } from '@chakra-ui/react';
import { publicApi, PublicCategoryListResponseCategory } from '@netiva/classifieds-api';
import { useUpdateEffectDeepCompare } from '@netiva/classifieds-common';
import { FormInput, FormSelect, FormSwitch, RenderOption, SelectOption } from '@netiva/classifieds-ui';

import { useAppSelector } from '@/store';

import { useAdListSearchParams } from '../hooks';
import { AdListSearchParams } from '../types';
import { useDebounced, useMatomoTagManager } from '@/hooks';

type FilterProps = {
  categoryCounts: {
    [key: string]: number;
  };
};

type CategoryOption = SelectOption<number> & {
  level: number;
};

export const Filter: FC<FilterProps> = ({ categoryCounts }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useAdListSearchParams();

  const { trackMatomoEvent } = useMatomoTagManager();

  const { data: categories, isLoading: isLoadingCategories } = publicApi.useGetCategories({
    parentId: 0,
    recursive: true,
  });

  const { platforms } = useAppSelector((state) => state.global);
  const { hasMemberContract } = useAppSelector((state) => state.account);
  const { isLoading: isLoadingPlatforms } = publicApi.useGetConfigurationPlatforms({});

  const form = useForm<AdListSearchParams>({
    values: {
      ...searchParams,
      // provide default values for undefined params to ensure controlled components
      categoryId: searchParams.categoryId || 0,
      platform: searchParams.platform || '',
      query: searchParams.query || '',
      topListed: searchParams.topListed || false,
    },
  });

  const currentValues = useWatch({ control: form.control });
  const prevValuesRef = useRef<AdListSearchParams>(form.getValues());

  const debouncedValues = useDebounced(currentValues);

  useUpdateEffectDeepCompare(() => {
    const prev = prevValuesRef.current;
    const curr = currentValues;

    // comparison of updated fields, used for tracking purposes
    const diffs: string[] = [];

    if (curr.query !== prev.query) {
      diffs.push('Keywords');
    }

    if (curr.categoryId !== prev.categoryId) {
      // check if actual change on category happened or is category field cleared with platform field update
      const isSyntheticReset = prev.categoryId === undefined && curr.categoryId === 0;

      if (!isSyntheticReset && curr.platform === prev.platform) {
        diffs.push('Category');
      }
    }

    if (curr.platform !== prev.platform) {
      diffs.push('Platform');
    }

    if (curr.topListed !== prev.topListed && curr.platform === prev.platform) {
      diffs.push('TopListing');
    }

    if (diffs.length > 0) {
      diffs.forEach((field) => {
        trackMatomoEvent({
          trackingCategory: 'Interaction_PI-MyListing',
          trackingAction: 'Filter',
          trackingName: `Filter_${field}`,
        });
      });
    }

    let next = curr;

    if (curr.platform !== prev.platform) {
      next = { ...curr, categoryId: undefined };
    }

    if (next.pageIndex !== prev.pageIndex) {
      setSearchParams(next);
    } else {
      setSearchParams({ ...next, pageIndex: 0 });
    }

    prevValuesRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValues]);

  const categoryOptions = useMemo<CategoryOption[]>(
    () =>
      categories
        ? [
            { label: t('form.select.all'), value: 0, level: 0 },
            ...buildCategoryOptions(categories.categories, categoryCounts),
          ]
        : [],
    [categories, categoryCounts, t]
  );

  const platformOptions = useMemo<SelectOption[]>(
    () => [
      { label: t('form.select.all'), value: '' },
      ...(platforms.map((p) => ({
        label: p.name,
        value: p.key,
      })) || []),
    ],
    [platforms, t]
  );

  const renderOption = useCallback<RenderOption<CategoryOption>>(
    ({ children, option }) => (
      <Box as="span" pl={option.level * 2}>
        {children}
      </Box>
    ),
    []
  );

  return (
    <Flex gap={4} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
      <FormProvider {...form}>
        <FormInput<AdListSearchParams> name="query" label={t('myads.filter.text')} />
        <FormSelect<AdListSearchParams, 'categoryId', CategoryOption>
          name="categoryId"
          label={t('myads.filter.category')}
          options={categoryOptions}
          isLoading={isLoadingCategories}
          isClearable
          renderOption={renderOption}
        />
        {platforms.length > 1 && (
          <FormSelect<AdListSearchParams>
            name="platform"
            label={t('myads.filter.platform')}
            options={platformOptions}
            isLoading={isLoadingPlatforms}
            isClearable
          />
        )}
        {hasMemberContract && <FormSwitch<AdListSearchParams> name="topListed" label={t('myads.filter.topListed')} />}
      </FormProvider>
    </Flex>
  );
};

function buildCategoryOptions(
  categories: PublicCategoryListResponseCategory[],
  counts: Record<string, number>,
  level = 0
): CategoryOption[] {
  return categories.flatMap((cat) => {
    const children = cat.children ? buildCategoryOptions(cat.children, counts, level + 1) : [];

    const count = counts[cat.id];

    const parentOption: CategoryOption = {
      label: cat.name || '-',
      value: cat.id,
      isDisabled: true,
      level: level,
    };

    const selfOption: CategoryOption[] = count
      ? [
          {
            label: `${cat.name} (${count})`,
            value: cat.id,
            level: level,
          },
        ]
      : [];

    if (children.length > 0) {
      return [parentOption, ...children];
    } else {
      return selfOption;
    }
  });
}
